/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactNode, useEffect, useState } from "react";
import { Button, Form, Modal, ModalProps, Spinner } from "react-bootstrap";
import { ELECTRICITY_AGREEMENT_STATUS_MAP, ElectricityAgreementStatus, IElectricityAgreement } from "../models";
import { ElectricityPackagesService } from "components/ElectricityPackages/ElectricityPackages.service";
import { FormContentBlock } from "components/common/FormContentBlock";
import Select from "react-select";
import DatePicker from "react-date-picker";
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { ReactComponent as ClearIcon } from '../../../assets/icons/close.svg';
import { ELECTRICITY_PACKAGE_EARLY_TERMINATION_FEE_TYPES, ElectricityPackageEarlyTerminationFeeType, ElectricityPackageType, IElectricityPackage } from "components/ElectricityPackages/models";
import { SelectOption } from "common/common-models";
import { isNullOrUndefined } from "common/data-util";
import { isFixedPackage } from "components/ElectricityAgreementRenewalPlans/modals/UpdateElectricityAgreementRenewalPlanModal";
import { ElectricityAgreementsService } from "../ElectricityAgreements.service";
import moment from "moment";
import { isoDateFormat } from "common/dateFormatterConfig";
import { onNumberChange } from "helpers";
import { toast } from "react-toastify";
import { validateEarlyTermination } from "common/validator";
import { isJointInvoicePossibleForAgreement } from "helpers/validator";
import ApartmentAgreementsBlock from "../ApartmentAgreements/ApartmentAgreementsBlock";

interface UpdateElectricityAgreementModalProps extends ModalProps {
  agreement: IElectricityAgreement;
}

interface UpdateElectricityAgreementModalForm {
  validated: boolean;
  status: ElectricityAgreementStatus;
  startDate: string;
  endDate?: string;
  consumptionPackageId: number;
  consumptionMarginalCentsKwh?: number;
  basePriceCentsKwh?: number;
  dayTariffCentsKwh?: number;
  nightTariffCentsKwh?: number;
  monthlyFeeEuros?: number;
  earlyTerminationFeeType?: ElectricityPackageEarlyTerminationFeeType;
  earlyTerminationFee?: number;
  estimatedConsumptionVolumeMwh?: number;
  estimatedConsumptionDayVolumeMwh?: number;
  estimatedConsumptionNightVolumeMwh?: number;
  marketPricePackage: boolean;
  fixedPricePackage: boolean;
  twoTariffPackage: boolean;
  productionPackageId?: number;
  productionMarginalCentsKwh?: number;
}

const ELECTRICITY_AGREEMENT_STATUS_TYPES = [
  { value: ElectricityAgreementStatus.ACTIVE, label: ELECTRICITY_AGREEMENT_STATUS_MAP[ElectricityAgreementStatus.ACTIVE] },
  { value: ElectricityAgreementStatus.CANCELED, label: ELECTRICITY_AGREEMENT_STATUS_MAP[ElectricityAgreementStatus.CANCELED] },
  { value: ElectricityAgreementStatus.ENDED, label: ELECTRICITY_AGREEMENT_STATUS_MAP[ElectricityAgreementStatus.ENDED] }
];

export function UpdateElectricityAgreementModal(props: UpdateElectricityAgreementModalProps) {
    const [form, setForm] = useState<UpdateElectricityAgreementModalForm>({
        validated: false,
        status: props.agreement.status,
        startDate: props.agreement.startDate,
        endDate: props.agreement.endDate,
        productionPackageId: props.agreement.productionPackage?.id,
        productionMarginalCentsKwh: props.agreement.productionMarginal,
        consumptionPackageId: props.agreement.consumptionPackage.id,
        consumptionMarginalCentsKwh: props.agreement.consumptionMarginal,
        basePriceCentsKwh: props.agreement.basePriceCentsKwh ?? props.agreement.consumptionPackage.basePriceCentsKwh,
        dayTariffCentsKwh: props.agreement.dayTariffCentsKwh ?? props.agreement.consumptionPackage.dayTariffCentsKwh,
        nightTariffCentsKwh: props.agreement.nightTariffCentsKwh ?? props.agreement.consumptionPackage.nightTariffCentsKwh,
        monthlyFeeEuros: props.agreement.monthlyFeeEuros ?? props.agreement.consumptionPackage.monthlyFeeEuros,
        earlyTerminationFeeType: props.agreement.consumptionPackage.earlyTerminationFeeType,
        earlyTerminationFee: props.agreement.earlyTerminationFee ?? props.agreement.consumptionPackage.earlyTerminationFee,
        estimatedConsumptionVolumeMwh: props.agreement.estimatedConsumptionVolumeMwh,
        estimatedConsumptionDayVolumeMwh: props.agreement.estimatedConsumptionDayVolumeMwh,
        estimatedConsumptionNightVolumeMwh: props.agreement.estimatedConsumptionNightVolumeMwh,
        marketPricePackage: props.agreement.consumptionPackage.marketPricePackage,
        fixedPricePackage: isFixedPackage(props.agreement.consumptionPackage),
        twoTariffPackage: props.agreement.consumptionPackage.twoTariffPackage
    });

    const [saving, setSaving] = useState(false);
    const [consumptionPackages, setConsumptionPackages] = useState<IElectricityPackage[]>([]);
    
    const isJointInvoicePossible = isJointInvoicePossibleForAgreement(props.agreement);

    useEffect(() => {
        (async () => {
          const data = await ElectricityPackagesService.getElectricityPackages({
                page: 0,
                size: 999
          });

          const packages = data.content
            .filter((pckg: IElectricityPackage) => pckg.jointInvoicePackage === isJointInvoicePossible && !pckg.dynamicPackage)
            .filter((pckg: IElectricityPackage) => pckg.type === ElectricityPackageType.CONSUMPTION);

          const matchingPackage = packages.find((pckg) => pckg.id === props.agreement.consumptionPackage.id);

          setConsumptionPackages(matchingPackage ? packages : [...packages, props.agreement.consumptionPackage]);
        })()
    }, []);

    const setPackageValue = (e : any) => {
      const p = consumptionPackages.find(p => p.id === e.value);
      if(!p) return;

      setForm({
        ...form, 
        consumptionPackageId: p.id,
        basePriceCentsKwh: p.basePriceCentsKwh,
        dayTariffCentsKwh: p.dayTariffCentsKwh,
        nightTariffCentsKwh: p.nightTariffCentsKwh,
        monthlyFeeEuros: p.monthlyFeeEuros ?? 0,
        earlyTerminationFeeType: p.earlyTerminationFeeType,
        earlyTerminationFee: p.earlyTerminationFee,
        estimatedConsumptionVolumeMwh: undefined,
        estimatedConsumptionDayVolumeMwh: undefined,
        estimatedConsumptionNightVolumeMwh: undefined,
        marketPricePackage: p.marketPricePackage,
        fixedPricePackage: isFixedPackage(p),
        twoTariffPackage: p.twoTariffPackage
      })
    }

    const validateSubmit = () => {
        if (validate()) submit();
    }

    const validate = (): boolean => {
        setForm({ ...form, validated: true });

        if (!form.status) return false;
        if (!form.startDate) return false;
        if (!form.consumptionPackageId) return false;
        if (isNullOrUndefined(form.monthlyFeeEuros)) return false;
        if (isNullOrUndefined(form.consumptionMarginalCentsKwh)) return false;
        if (form.productionPackageId && isNullOrUndefined(form.productionMarginalCentsKwh)) return false;
        if (form.fixedPricePackage && isNullOrUndefined(form.basePriceCentsKwh)) return false;
        if (form.twoTariffPackage && isNullOrUndefined(form.dayTariffCentsKwh) && isNullOrUndefined(form.nightTariffCentsKwh)) return false;
        if (!validateEarlyTermination(
          form.earlyTerminationFeeType,
          form.earlyTerminationFee,
          form.twoTariffPackage,
          form.estimatedConsumptionVolumeMwh,
          form.estimatedConsumptionDayVolumeMwh,
          form.estimatedConsumptionNightVolumeMwh
        )) return false;
        return true;
    }

    const submit = async () => {
        setSaving(true);

        try {
          await ElectricityAgreementsService.updateElectricityAgreement(
            props.agreement.id,
            {
              status: form.status,
              startDate: moment(form.startDate).startOf('day').add(3, 'hours').format(isoDateFormat),
              endDate: form.endDate ? moment(form.endDate).startOf('day').add(3, 'hours').format(isoDateFormat) : undefined,
              consumptionPackageId: form.consumptionPackageId,
              basePriceCentsKwh: form.basePriceCentsKwh,
              dayTariffCentsKwh: form.dayTariffCentsKwh,
              nightTariffCentsKwh: form.nightTariffCentsKwh,
              monthlyFeeEuros: form.monthlyFeeEuros,
              earlyTerminationFee: form.earlyTerminationFee,
              estimatedConsumptionVolumeMwh: form.estimatedConsumptionVolumeMwh,
              estimatedConsumptionDayVolumeMwh: form.estimatedConsumptionDayVolumeMwh,
              estimatedConsumptionNightVolumeMwh: form.estimatedConsumptionNightVolumeMwh,
              consumptionMarginalCentsKwh: form.consumptionMarginalCentsKwh!,
              productionMarginalCentsKwh: form.productionMarginalCentsKwh,
              paidFromCustomersAdvance: props.agreement.paidFromCustomersAdvance,
              invoiceRecipientEmail: props.agreement.invoiceRecipientEmail
            }
          )

          toast("Lepingu uuendamine õnnestus", {
            position: "top-right",
            type: 'success'
          })

          props.onSuccess?.();

          setTimeout(() => {
            props.handleModalHide();
          }, 500);  
        } catch(e) {
          console.error(e)
          toast("Lepingu uuendamine ebaõnnestus", {
            position: "top-right",
            type: 'error'
          })
        } finally {
            setSaving(false);
        }
    }

    const packageOptions : SelectOption<number>[] = consumptionPackages.map(p => ({ value: p.id, label: p.nameEt }));

    const currentStatusType = ELECTRICITY_AGREEMENT_STATUS_TYPES.find(type => type.value === form.status);
    const currentPackageOption = packageOptions.find(p => p.value === form.consumptionPackageId);
    const currentEarlyTerminationFeeType = ELECTRICITY_PACKAGE_EARLY_TERMINATION_FEE_TYPES.find(p => p.value === form.earlyTerminationFeeType);

    return (
      <Modal
        show={true}
        size={"xl"}
        dialogClassName="bg-light"
        onHide={props.handleModalHide}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Muuda lepingut
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form className="update-electricity-agreement__container" noValidate validated={form.validated}>
              <FormContentBlock title="Leping">
                  <h2 className="row col-12">Lepingu number: {props.agreement.contractNumber}</h2>
                  <FormRow>
                    <FormColumn>
                      <Form.Label>Staatus*</Form.Label>
                      <Select
                          required
                          options={ELECTRICITY_AGREEMENT_STATUS_TYPES}
                          value={currentStatusType}
                          onChange={(e: any) => setForm({...form, status: e.value})}
                          placeholder='Vali staatus'
                          className={`w-100 form-control form-select ${!form.status && form.validated ? ' is-invalid' : ''}`}
                      />
                      <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback> 
                    </FormColumn>
                  </FormRow>
                  <FormRow>
                    <FormColumn>
                      <Form.Label htmlFor="contract-start">Lepingu algus*</Form.Label>
                      <DatePicker
                          required
                          className={`d-block ${!form.startDate && form.validated ? ' invalid is-invalid' : ''}`}
                          value={new Date(form.startDate)}
                          locale='et'
                          clearIcon={null}
                          calendarIcon={<CalendarIcon />}
                          format='dd.MM.yyyy'
                          onChange={(e: any) => {
                              if (!e) return;
                              setForm({
                                  ...form, 
                                  startDate: new Date(e).toString(),
                              })
                          }}
                      />
                      <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                    </FormColumn>
                    <FormColumn>
                      <Form.Label htmlFor="contract-start">Lepingu lõpp</Form.Label>
                      <DatePicker
                          className="d-block"
                          value={form.endDate ? new Date(form.endDate) : undefined}
                          locale='et'
                          clearIcon={<ClearIcon />}
                          calendarIcon={<CalendarIcon />}
                          format='dd.MM.yyyy'
                          onChange={(e: any) => {
                              setForm({
                                  ...form, 
                                  endDate: e ? new Date(e).toString() : undefined,
                              })
                          }}
                      />
                    </FormColumn>
                </FormRow>
                <FormRow>
                    <FormColumn>
                      <Form.Label>Pakett*</Form.Label>
                      <Select
                          required
                          options={packageOptions}
                          value={currentPackageOption}
                          onChange={(e: any) => setPackageValue(e)}
                          placeholder='Vali elektripakett'
                          className={`w-100 form-control form-select ${!form.consumptionPackageId && form.validated ? ' is-invalid' : ''}`}
                      />
                      <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                    </FormColumn>
                     <FormColumn>
                        <Form.Label>Kuutasu (€/kuu)*</Form.Label>
                        <Form.Control
                            type="number"
                            step={0.001}
                            value={form.monthlyFeeEuros}
                            onChange={(e: any) => setForm({
                                ...form,
                                monthlyFeeEuros: onNumberChange(e),
                            })}
                            required
                        />
                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                      </FormColumn>
                </FormRow>
                {
                  form?.twoTariffPackage && (
                    <FormRow>
                      <FormColumn>
                        <Form.Label>Päeva tariif (s/kWh)*</Form.Label>
                        <Form.Control
                            type="number"
                            step={0.001}
                            value={form.dayTariffCentsKwh}
                            onChange={(e: any) => setForm({
                                ...form,
                                dayTariffCentsKwh: onNumberChange(e)
                            })}
                            required
                        />
                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                      </FormColumn>
                      <FormColumn>
                        <Form.Label>Öö tariif (s/kWh)*</Form.Label>
                        <Form.Control
                            type="number"
                            step={0.001}
                            value={form.nightTariffCentsKwh}
                            onChange={(e: any) => setForm({
                                ...form,
                                nightTariffCentsKwh: onNumberChange(e)
                            })}
                            required
                        />
                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                      </FormColumn>
                    </FormRow>
                  )
                }
                {
                  form.fixedPricePackage && (
                      <FormRow>
                        <div className="col-12 col-md-6">
                          <Form.Label>Baashind KM-ta (s/kWh)*</Form.Label>
                          <Form.Control
                              type="number"
                              step={0.001}
                              value={form.basePriceCentsKwh}
                              onChange={(e: any) => setForm({
                                  ...form,
                                  basePriceCentsKwh: onNumberChange(e)
                              })}
                              required
                          />
                          <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                      </FormRow>
                  )
                }
                <FormRow>
                  <div className="col-12 col-md-6">
                    <Form.Label>Tarbimise marginaal (s/kWh)*</Form.Label>
                    <Form.Control
                        type="number"
                        step={0.001}
                        value={form.consumptionMarginalCentsKwh}
                        onChange={(e: any) => setForm({
                            ...form,
                            consumptionMarginalCentsKwh: onNumberChange(e)
                        })}
                        required
                    />
                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                  </div>
                  {
                    form.productionPackageId ? (
                      <div className="col-12 col-md-6">
                        <Form.Label>Tootmise marginaal (s/kWh)*</Form.Label>
                        <Form.Control
                            type="number"
                            step={0.001}
                            value={form.productionMarginalCentsKwh}
                            onChange={(e: any) => setForm({
                                ...form,
                                productionMarginalCentsKwh: onNumberChange(e)
                            })}
                            required
                        />
                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                      </div>
                    ) : null
                  }
                </FormRow>
                {
                  !!form.earlyTerminationFeeType && (
                    <>
                      <FormRow>
                        <div className="col-12 col-md-6">
                          <Form.Label>Ennetähtaegse lõpetamise tasu tüüp</Form.Label>
                          <Select
                              className="form-select" 
                              options={ELECTRICITY_PACKAGE_EARLY_TERMINATION_FEE_TYPES}
                              value={currentEarlyTerminationFeeType}
                              placeholder=""
                              isDisabled
                          />
                        </div>
                        {
                          form.earlyTerminationFeeType === ElectricityPackageEarlyTerminationFeeType.FIXED_PER_LEFTOVER_MONTH ? (
                            <div className="col-12 col-md-6">
                              <Form.Label>
                                Lõpetamise tasu KM-ta (€)*
                              </Form.Label>
                              <Form.Control
                                  type="number"
                                  step={0.001}
                                  value={form.earlyTerminationFee}
                                  onChange={(e: any) => setForm({
                                      ...form,
                                      earlyTerminationFee: onNumberChange(e)
                                  })}
                                  required
                              />
                              <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </div>
                          ) : null
                        }
                      </FormRow>
                      {
                        form.earlyTerminationFeeType === ElectricityPackageEarlyTerminationFeeType.PERCENTAGE_OF_VOLUME ? (
                          <>
                            <FormRow>
                              <div className="col-12 col-md-6">
                                <Form.Label>
                                  Tarbimismahu protsent (%)*
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={form.earlyTerminationFee}
                                    onChange={(e: any) => setForm({
                                        ...form,
                                        earlyTerminationFee: onNumberChange(e)
                                    })}
                                    required
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                              </div>
                              {
                                !form.twoTariffPackage && (
                                  <div className="col-12 col-md-6">
                                    <Form.Label>
                                      Hinnanguline tarbimise maht (MWh)*
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        step={0.001}
                                        value={form.estimatedConsumptionVolumeMwh}
                                        onChange={(e: any) => setForm({
                                            ...form,
                                            estimatedConsumptionVolumeMwh: onNumberChange(e)
                                        })}
                                        required
                                    />
                                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                  </div>
                                )
                              }
                            </FormRow>
                            {
                              form.twoTariffPackage && (
                                <FormRow>
                                  <div className="col-12 col-md-6">
                                    <Form.Label>
                                      Hinnanguline päeva tarbimise maht (MWh)*
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        step={0.001}
                                        value={form.estimatedConsumptionDayVolumeMwh}
                                        onChange={(e: any) => setForm({
                                            ...form,
                                            estimatedConsumptionDayVolumeMwh: onNumberChange(e)
                                        })}
                                        required
                                    />
                                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                  </div>
                                  <div className="col-12 col-md-6">
                                    <Form.Label>
                                      Hinnanguline öine tarbimise maht (MWh)*
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        step={0.001}
                                        value={form.estimatedConsumptionNightVolumeMwh}
                                        onChange={(e: any) => setForm({
                                            ...form,
                                            estimatedConsumptionNightVolumeMwh: onNumberChange(e)
                                        })}
                                        required
                                    />
                                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                  </div>
                                </FormRow>
                              )
                            }
                          </>
                        ) : null
                      }
                    </>
                  )
                }
              </FormContentBlock>
            </Form>
            {props.agreement.customer.apartmentAssociation && (
              <ApartmentAgreementsBlock
                agreementId={props.agreement.id}
                defaultDateStart={props.agreement.startDate}
                parentDateEnd={props.agreement.endDate}
              />
            )}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-row align-items-end justify-content-end flex-wrap mb-1">
            <Button 
              variant="info"
              onClick={() => props.handleModalHide()}
              type="button"
              className={"d-flex align-items-center"}
            >
              <span className='px-2'>Sulge</span>
            </Button>
            <Button 
              variant="primary" 
              onClick={validateSubmit} 
              type="button" 
              disabled={saving}
              className="d-flex align-items-center"
            >
              {
                saving ? (
                  <>
                    <span className='px-2'>Salvestamine…</span>
                    <Spinner size={"sm"} animation={"border"}/>
                  </>
                ) : (
                  <span className='px-2'>Muuda</span>
                )
              }
            </Button>
        </Modal.Footer>
      </Modal>    
    );
}

const FormRow = ({children} : {children: ReactNode}) => {
  return (
    <div className="row mb-2">
      { children }
    </div>
  )
}

const FormColumn = ({children} : {children: ReactNode}) => {
  return (
    <div className="col-12 col-md-6">
      { children }
    </div>
  )
}